import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  TextField,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tooltip,
  Badge,
  LinearProgress,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  ThumbUp as VoteIcon,
  Visibility as RevealIcon,
  Refresh as ResetIcon,
  CheckCircle as CompleteIcon,
  People as PeopleIcon,
  SentimentSatisfied as SentimentIcon,
  Assignment as ActionIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';

interface RetrospectiveResponse {
  id: string;
  text: string;
  authorId: string;
  authorName: string;
  votes: number;
  createdAt: string;
}

interface Retrospective {
  id: string;
  title: string;
  description?: string;
  template: string;
  status: 'draft' | 'in_progress' | 'completed' | 'cancelled';
  responses?: { [categoryId: string]: RetrospectiveResponse[] };
  settings: {
    allowAnonymous: boolean;
    allowVoting: boolean;
    maxVotesPerPerson: number;
    requireAllParticipants: boolean;
  };
  facilitator: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface RetrospectiveSessionProps {
  retrospective: any;
  onComplete?: (retrospective: any) => void;
  onClose?: () => void;
}

const RetrospectiveSession: React.FC<RetrospectiveSessionProps> = ({
  retrospective,
  onComplete,
  onClose
}) => {
  const { user } = useAuth();
  const [currentRetrospective, setCurrentRetrospective] = useState<Retrospective>(retrospective);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Response states
  const [newResponses, setNewResponses] = useState<{ [categoryId: string]: string }>({});
  const [userVotes, setUserVotes] = useState<{ [responseId: string]: boolean }>({});
  
  // Sentiment states
  const [sentiments, setSentiments] = useState<{ [type: string]: number }>({});
  const [sentimentComments, setSentimentComments] = useState<{ [type: string]: string }>({});
  
  // Action item states
  const [actionItems, setActionItems] = useState<any[]>([]);
  const [newActionItem, setNewActionItem] = useState({
    title: '',
    description: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'critical',
    assignedToId: '',
    dueDate: ''
  });
  const [openActionItemModal, setOpenActionItemModal] = useState(false);

  const templateInfo = {
    start_stop_continue: {
      name: 'Start, Stop, Continue',
      categories: ['Start', 'Stop', 'Continue'],
      colors: ['#4caf50', '#f44336', '#2196f3']
    },
    four_ls: {
      name: '4Ls',
      categories: ['Liked', 'Learned', 'Lacked', 'Longed for'],
      colors: ['#4caf50', '#ff9800', '#f44336', '#9c27b0']
    },
    mad_sad_glad: {
      name: 'Mad, Sad, Glad',
      categories: ['Mad', 'Sad', 'Glad'],
      colors: ['#f44336', '#ff9800', '#4caf50']
    },
    kalm: {
      name: 'KALM',
      categories: ['Keep', 'Add', 'Less', 'More'],
      colors: ['#4caf50', '#2196f3', '#ff9800', '#9c27b0']
    },
    rose_thorn_bud: {
      name: 'Rose, Thorn, Bud',
      categories: ['Rose', 'Thorn', 'Bud'],
      colors: ['#e91e63', '#ff5722', '#4caf50']
    }
  };

  const sentimentTypes = [
    { id: 'mood', label: 'Team Mood', icon: '😊' },
    { id: 'confidence', label: 'Confidence Level', icon: '💪' },
    { id: 'satisfaction', label: 'Satisfaction', icon: '👍' },
    { id: 'stress', label: 'Stress Level', icon: '😰' },
    { id: 'energy', label: 'Energy Level', icon: '⚡' }
  ];

  const steps = [
    'Setup & Introduction',
    'Gather Feedback',
    'Vote & Discuss',
    'Sentiment Check',
    'Action Items',
    'Wrap Up'
  ];

  const loadRetrospective = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.get<Retrospective>(`/retrospectives/${retrospective.id}`);
      if (response.success && response.data) {
        setCurrentRetrospective(response.data);
      }
    } catch (err: any) {
      console.error('Error loading retrospective:', err);
      setError(err.response?.data?.message || 'Failed to load retrospective');
    } finally {
      setLoading(false);
    }
  }, [retrospective.id]);

  const loadActionItems = useCallback(async () => {
    try {
      const response = await apiService.get<any[]>(`/retrospectives/${retrospective.id}/action-items`);
      if (response.success && response.data) {
        setActionItems(response.data);
      }
    } catch (err: any) {
      console.error('Error loading action items:', err);
    }
  }, [retrospective.id]);

  useEffect(() => {
    loadRetrospective();
    loadActionItems();
  }, [loadRetrospective, loadActionItems]);

  const handleSubmitResponse = async (categoryId: string) => {
    const text = newResponses[categoryId]?.trim();
    if (!text) return;

    try {
      setLoading(true);
      const response = await apiService.post(`/retrospectives/${retrospective.id}/responses`, {
        categoryId,
        text
      });

      if (response.success) {
        setNewResponses(prev => ({ ...prev, [categoryId]: '' }));
        await loadRetrospective();
      }
    } catch (err: any) {
      console.error('Error submitting response:', err);
      setError(err.response?.data?.message || 'Failed to submit response');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (responseId: string) => {
    try {
      const response = await apiService.post(`/retrospectives/${retrospective.id}/vote`, {
        responseId
      });

      if (response.success) {
        setUserVotes(prev => ({ ...prev, [responseId]: !prev[responseId] }));
        await loadRetrospective();
      }
    } catch (err: any) {
      console.error('Error voting:', err);
      setError(err.response?.data?.message || 'Failed to vote');
    }
  };

  const handleSubmitSentiment = async (type: string) => {
    try {
      const response = await apiService.post(`/retrospectives/${retrospective.id}/sentiment`, {
        type,
        value: sentiments[type],
        comment: sentimentComments[type]
      });

      if (response.success) {
        setSentimentComments(prev => ({ ...prev, [type]: '' }));
      }
    } catch (err: any) {
      console.error('Error submitting sentiment:', err);
      setError(err.response?.data?.message || 'Failed to submit sentiment');
    }
  };

  const handleCreateActionItem = async () => {
    try {
      setLoading(true);
      const response = await apiService.post(`/retrospectives/${retrospective.id}/action-items`, {
        ...newActionItem,
        dueDate: newActionItem.dueDate ? new Date(newActionItem.dueDate).toISOString() : undefined
      });

      if (response.success) {
        setNewActionItem({
          title: '',
          description: '',
          priority: 'medium',
          assignedToId: '',
          dueDate: ''
        });
        setOpenActionItemModal(false);
        await loadActionItems();
      }
    } catch (err: any) {
      console.error('Error creating action item:', err);
      setError(err.response?.data?.message || 'Failed to create action item');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRetrospective = async () => {
    try {
      setLoading(true);
      const response = await apiService.post(`/retrospectives/${retrospective.id}/complete`);

      if (response.success) {
        if (onComplete) {
          onComplete(response.data);
        }
      }
    } catch (err: any) {
      console.error('Error completing retrospective:', err);
      setError(err.response?.data?.message || 'Failed to complete retrospective');
    } finally {
      setLoading(false);
    }
  };

  const getTemplateData = () => {
    return templateInfo[currentRetrospective.template as keyof typeof templateInfo] || {
      name: 'Custom Template',
      categories: [],
      colors: []
    };
  };

  const getCategoryColor = (categoryIndex: number) => {
    const colors = getTemplateData().colors;
    return colors[categoryIndex % colors.length] || '#757575';
  };

  const isFacilitator = user?.id === currentRetrospective.facilitator.id;

  if (loading && !currentRetrospective) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1">
            {currentRetrospective.title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {getTemplateData().name} • Facilitated by {currentRetrospective.facilitator.firstName} {currentRetrospective.facilitator.lastName}
          </Typography>
        </Box>
        {onClose && (
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Progress Stepper */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step Content */}
      <Box>
        {/* Step 0: Setup & Introduction */}
        {activeStep === 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Welcome to the Retrospective
              </Typography>
              <Typography variant="body1" paragraph>
                {currentRetrospective.description || 'Let\'s reflect on our sprint and identify areas for improvement.'}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Template: {getTemplateData().name}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Categories: {getTemplateData().categories.join(', ')}
              </Typography>
              <Box display="flex" gap={2} mt={3}>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(1)}
                >
                  Start Gathering Feedback
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Gather Feedback */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Share Your Thoughts
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Add your responses for each category. Be specific and constructive.
            </Typography>
            
            <Grid container spacing={3}>
              {getTemplateData().categories.map((category, index) => (
                <Grid item xs={12} md={6} key={category}>
                  <Card sx={{ borderLeft: `4px solid ${getCategoryColor(index)}` }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color={getCategoryColor(index)}>
                        {category}
                      </Typography>
                      
                      {/* Existing Responses */}
                      {currentRetrospective.responses?.[category] && (
                        <Box mb={2}>
                          {currentRetrospective.responses[category].map((response) => (
                            <Paper key={response.id} sx={{ p: 2, mb: 1, bgcolor: 'grey.50' }}>
                              <Typography variant="body2" paragraph>
                                {response.text}
                              </Typography>
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="caption" color="text.secondary">
                                  {response.authorName}
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Chip
                                    icon={<VoteIcon />}
                                    label={response.votes}
                                    size="small"
                                    color={userVotes[response.id] ? 'primary' : 'default'}
                                    onClick={() => handleVote(response.id)}
                                    disabled={!currentRetrospective.settings.allowVoting}
                                  />
                                </Box>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      )}

                      {/* Add New Response */}
                      <Box display="flex" gap={1}>
                        <TextField
                          fullWidth
                          placeholder={`What should we ${category.toLowerCase()}?`}
                          value={newResponses[category] || ''}
                          onChange={(e) => setNewResponses(prev => ({ ...prev, [category]: e.target.value }))}
                          multiline
                          rows={2}
                        />
                        <Button
                          variant="contained"
                          onClick={() => handleSubmitResponse(category)}
                          disabled={!newResponses[category]?.trim() || loading}
                        >
                          Add
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box display="flex" gap={2} mt={3}>
              <Button onClick={() => setActiveStep(0)}>Back</Button>
              <Button
                variant="contained"
                onClick={() => setActiveStep(2)}
              >
                Next: Vote & Discuss
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 2: Vote & Discuss */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Vote & Discuss
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Vote on the most important items and discuss them as a team.
            </Typography>

            <Grid container spacing={3}>
              {getTemplateData().categories.map((category, index) => (
                <Grid item xs={12} md={6} key={category}>
                  <Card sx={{ borderLeft: `4px solid ${getCategoryColor(index)}` }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color={getCategoryColor(index)}>
                        {category} - Top Items
                      </Typography>
                      
                      {currentRetrospective.responses?.[category] ? (
                        <List>
                          {currentRetrospective.responses[category]
                            .sort((a, b) => b.votes - a.votes)
                            .slice(0, 5)
                            .map((response) => (
                            <ListItem key={response.id} divider>
                              <ListItemText
                                primary={response.text}
                                secondary={`${response.votes} votes • ${response.authorName}`}
                              />
                              <ListItemSecondaryAction>
                                <Chip
                                  icon={<VoteIcon />}
                                  label={response.votes}
                                  size="small"
                                  color={userVotes[response.id] ? 'primary' : 'default'}
                                  onClick={() => handleVote(response.id)}
                                  disabled={!currentRetrospective.settings.allowVoting}
                                />
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No responses yet
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box display="flex" gap={2} mt={3}>
              <Button onClick={() => setActiveStep(1)}>Back</Button>
              <Button
                variant="contained"
                onClick={() => setActiveStep(3)}
              >
                Next: Sentiment Check
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 3: Sentiment Check */}
        {activeStep === 3 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Team Sentiment
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Rate how the team feels about different aspects of the sprint.
            </Typography>

            <Grid container spacing={3}>
              {sentimentTypes.map((sentiment) => (
                <Grid item xs={12} md={6} key={sentiment.id}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={2} mb={2}>
                        <Typography variant="h6">
                          {sentiment.icon} {sentiment.label}
                        </Typography>
                      </Box>
                      
                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Rate from 1 (Low) to 5 (High)
                        </Typography>
                        <Slider
                          value={sentiments[sentiment.id] || 3}
                          onChange={(_, value) => setSentiments(prev => ({ ...prev, [sentiment.id]: value as number }))}
                          min={1}
                          max={5}
                          step={1}
                          marks={[
                            { value: 1, label: '1' },
                            { value: 2, label: '2' },
                            { value: 3, label: '3' },
                            { value: 4, label: '4' },
                            { value: 5, label: '5' }
                          ]}
                        />
                      </Box>

                      <TextField
                        fullWidth
                        label="Comments (optional)"
                        multiline
                        rows={2}
                        value={sentimentComments[sentiment.id] || ''}
                        onChange={(e) => setSentimentComments(prev => ({ ...prev, [sentiment.id]: e.target.value }))}
                        sx={{ mb: 2 }}
                      />

                      <Button
                        variant="outlined"
                        onClick={() => handleSubmitSentiment(sentiment.id)}
                        disabled={!sentiments[sentiment.id]}
                      >
                        Submit
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box display="flex" gap={2} mt={3}>
              <Button onClick={() => setActiveStep(2)}>Back</Button>
              <Button
                variant="contained"
                onClick={() => setActiveStep(4)}
              >
                Next: Action Items
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 4: Action Items */}
        {activeStep === 4 && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6">
                Action Items
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenActionItemModal(true)}
              >
                Add Action Item
              </Button>
            </Box>

            {actionItems.length === 0 ? (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No action items yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Create action items to track improvements for the next sprint.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenActionItemModal(true)}
                  >
                    Create First Action Item
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Grid container spacing={2}>
                {actionItems.map((actionItem) => (
                  <Grid item xs={12} key={actionItem.id}>
                    <Card>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box flexGrow={1}>
                            <Typography variant="h6" gutterBottom>
                              {actionItem.title}
                            </Typography>
                            {actionItem.description && (
                              <Typography variant="body2" color="text.secondary" paragraph>
                                {actionItem.description}
                              </Typography>
                            )}
                            <Box display="flex" gap={1} flexWrap="wrap">
                              <Chip
                                label={actionItem.status}
                                size="small"
                                color={actionItem.status === 'completed' ? 'success' : 'default'}
                              />
                              <Chip
                                label={actionItem.priority}
                                size="small"
                                variant="outlined"
                              />
                              {actionItem.assignedTo && (
                                <Chip
                                  label={`Assigned to ${actionItem.assignedTo.firstName} ${actionItem.assignedTo.lastName}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              {actionItem.dueDate && (
                                <Chip
                                  label={`Due: ${new Date(actionItem.dueDate).toLocaleDateString()}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                          {isFacilitator && (
                            <Box>
                              <IconButton size="small">
                                <EditIcon />
                              </IconButton>
                              <IconButton size="small">
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            <Box display="flex" gap={2} mt={3}>
              <Button onClick={() => setActiveStep(3)}>Back</Button>
              <Button
                variant="contained"
                onClick={() => setActiveStep(5)}
              >
                Next: Wrap Up
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 5: Wrap Up */}
        {activeStep === 5 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Retrospective Complete
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Great job! Let's summarize what we've accomplished and complete the retrospective.
            </Typography>

            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Summary
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • {Object.keys(currentRetrospective.responses || {}).length} categories discussed
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • {actionItems.length} action items created
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Team sentiment captured
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Next Steps
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Review action items in the next sprint planning
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Follow up on high-priority items
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Track progress in future retrospectives
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box display="flex" gap={2}>
              <Button onClick={() => setActiveStep(4)}>Back</Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<CompleteIcon />}
                onClick={handleCompleteRetrospective}
                disabled={loading}
              >
                Complete Retrospective
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Create Action Item Modal */}
      <Dialog open={openActionItemModal} onClose={() => setOpenActionItemModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Action Item</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Title"
              value={newActionItem.title}
              onChange={(e) => setNewActionItem(prev => ({ ...prev, title: e.target.value }))}
              required
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={newActionItem.description}
              onChange={(e) => setNewActionItem(prev => ({ ...prev, description: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={newActionItem.priority}
                label="Priority"
                onChange={(e) => setNewActionItem(prev => ({ ...prev, priority: e.target.value as any }))}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Due Date"
              type="date"
              value={newActionItem.dueDate}
              onChange={(e) => setNewActionItem(prev => ({ ...prev, dueDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenActionItemModal(false)}>Cancel</Button>
          <Button
            onClick={handleCreateActionItem}
            variant="contained"
            disabled={!newActionItem.title || loading}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RetrospectiveSession;
